import { getWidgetConfigURL, socketURL } from "./url/URL";

interface LoaderMountConfig {
  companyId: string;
  socketUrl: string;
  position?: "bottom-right" | "bottom-left";
  themePreset?: string;
  assistantName?: string;
  logoUrl?: string;
  welcomeMessage?: string;
  tooltipMessage?: string;
  brandLabel?: string;
}

interface BootstrapGlobal {
  company_id: string;
}

interface WidgetApiGlobal {
  init?: (config: LoaderMountConfig) => unknown;
  mount?: (config: LoaderMountConfig) => unknown;
  mountWidget?: (config: LoaderMountConfig) => unknown;
}

interface WidgetConfigResponse {
  company_id?: string;
  theme_preset?: string;
  themePreset?: string;
  branding?: {
    name?: string;
    logo_url?: string;
  };
  appearance?: {
    position?: "bottom-right" | "bottom-left";
    theme_preset?: string;
    themePreset?: string;
    welcome_message?: string;
    tooltip_message?: string;
    tooltipMessage?: string;
  };
  position?: string;
  config?: WidgetConfigResponse;
  widget_config?: WidgetConfigResponse;
  data?: WidgetConfigResponse;
}

const WIDGET_GLOBAL_READY_EVENT = "verity:widget:ready";

function getCurrentScript(): HTMLScriptElement | null {
  return document.currentScript instanceof HTMLScriptElement
    ? document.currentScript
    : document.querySelector("script[src*='widget.js']");
}

function parseBootstrap(): { companyId: string; widgetUrlOverride?: string } {
  const script = getCurrentScript();
  const globalVal = (window as Window & { VerityWidget?: unknown })
    .VerityWidget;

  if (
    typeof globalVal === "object" &&
    globalVal !== null &&
    "company_id" in globalVal
  ) {
    const companyId = (globalVal as BootstrapGlobal).company_id;
    if (typeof companyId === "string" && companyId.trim()) {
      return {
        companyId: companyId.trim(),
        widgetUrlOverride: script?.dataset.verityWidgetUrl,
      };
    }
  }

  if (script?.dataset.verityCompanyId) {
    return {
      companyId: script.dataset.verityCompanyId,
      widgetUrlOverride: script.dataset.verityWidgetUrl,
    };
  }

  throw new Error("Verity loader requires window.VerityWidget.company_id");
}

function buildWidgetUrl(
  script: HTMLScriptElement | null,
  override?: string,
): string {
  if (override) {
    return override;
  }

  if (script?.dataset.verityWidgetUrl) {
    return script.dataset.verityWidgetUrl;
  }

  const src = script?.src || "./widget.js";
  const scriptUrl = new URL(src, window.location.href);
  return new URL("./verity-widget.iife.js", scriptUrl).toString();
}

async function fetchJson(url: string): Promise<unknown> {
  const requestUrl = new URL(url, window.location.href);
  requestUrl.searchParams.set("_ts", String(Date.now()));

  const response = await fetch(requestUrl.toString(), {
    method: "GET",
    cache: "no-store",
    headers: {
      Accept: "application/json",
      "Cache-Control": "no-cache, no-store, must-revalidate",
      Pragma: "no-cache",
    },
  });
  if (!response.ok) {
    throw new Error(`HTTP ${response.status} from ${url}`);
  }

  return response.json();
}

function unwrapData<T>(input: unknown): T {
  if (typeof input === "object" && input !== null && "data" in input) {
    return (input as { data: T }).data;
  }
  return input as T;
}

function resolveConfigShape(input: unknown): WidgetConfigResponse {
  const root = unwrapData<WidgetConfigResponse>(input);

  if (root && typeof root === "object") {
    if (root.widget_config && typeof root.widget_config === "object") {
      return root.widget_config;
    }
    if (root.config && typeof root.config === "object") {
      return root.config;
    }
    if (root.data && typeof root.data === "object") {
      const nested = root.data;
      if (nested.widget_config && typeof nested.widget_config === "object") {
        return nested.widget_config;
      }
      if (nested.config && typeof nested.config === "object") {
        return nested.config;
      }
      return nested;
    }
  }

  return root;
}

function normalizePosition(value: unknown): "bottom-right" | "bottom-left" {
  if (typeof value !== "string") {
    return "bottom-right";
  }
  return value.toLowerCase() === "bottom-left" ? "bottom-left" : "bottom-right";
}

function mapMountConfig(
  companyId: string,
  widgetDataRaw: unknown,
): LoaderMountConfig {
  const widgetData = resolveConfigShape(widgetDataRaw);

  const branding = widgetData.branding ?? {};
  const appearance = widgetData.appearance ?? {};
  const rawPosition = appearance.position ?? widgetData.position;
  const resolvedPosition = normalizePosition(rawPosition);
  const themePreset =
    typeof (
      widgetData.theme_preset ??
      widgetData.themePreset ??
      appearance.theme_preset ??
      appearance.themePreset
    ) === "string" &&
    (
      widgetData.theme_preset ??
      widgetData.themePreset ??
      appearance.theme_preset ??
      appearance.themePreset
    )?.trim()
      ? (
          widgetData.theme_preset ??
          widgetData.themePreset ??
          appearance.theme_preset ??
          appearance.themePreset
        )!.trim()
      : undefined;

  return {
    companyId,
    socketUrl: socketURL,
    position: resolvedPosition,
    themePreset: themePreset ?? "slate-calm",
    assistantName: branding.name || "Verity AI Assistant",
    logoUrl: branding.logo_url,
    welcomeMessage: appearance.welcome_message || "Hi! How can I help?",
    tooltipMessage:
      appearance.tooltip_message ??
      appearance.tooltipMessage ??
      "Need help? Chat with us",
    brandLabel: "POWERED BY VERITY AI",
  };
}

function initWhenReady(config: LoaderMountConfig): void {
  const tryInit = (): boolean => {
    const api = (window as Window & { VerityWidget?: unknown }).VerityWidget as
      | WidgetApiGlobal
      | undefined;
    const initFn = api?.init ?? api?.mount ?? api?.mountWidget;
    if (!initFn) {
      return false;
    }
    initFn(config);
    return true;
  };

  if (tryInit()) {
    return;
  }

  let attempts = 0;
  const maxAttempts = 30;
  const retryTimer = window.setInterval(() => {
    attempts += 1;
    if (tryInit() || attempts >= maxAttempts) {
      window.clearInterval(retryTimer);
    }
  }, 50);

  window.addEventListener(
    WIDGET_GLOBAL_READY_EVENT,
    () => {
      void tryInit();
    },
    { once: true },
  );
}

function loadWidgetScript(widgetUrl: string): Promise<void> {
  if (document.querySelector(`script[data-verity-widget-src="${widgetUrl}"]`)) {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = widgetUrl;
    script.async = true;
    script.dataset.verityWidgetSrc = widgetUrl;
    script.onload = () => {
      window.dispatchEvent(new CustomEvent(WIDGET_GLOBAL_READY_EVENT));
      resolve();
    };
    script.onerror = () =>
      reject(new Error(`Failed to load Verity widget bundle: ${widgetUrl}`));
    document.head.appendChild(script);
  });
}

(async function bootstrap() {
  try {
    const script = getCurrentScript();
    const { companyId, widgetUrlOverride } = parseBootstrap();

    const widgetData = await fetchJson(getWidgetConfigURL(companyId));

    const mountConfig = mapMountConfig(companyId, widgetData);
    const widgetUrl = buildWidgetUrl(script, widgetUrlOverride);

    await loadWidgetScript(widgetUrl);
    initWhenReady(mountConfig);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("[Verity Widget Loader]", error);
  }
})();
